import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { setCircularLoading } from "../../reducers/UiReducer";
import { getService } from "../../reducers/service";
import { notify } from "../../reducers/NotifierReducer";
import { serviceNames } from "../../utils/constants";
import { PATH_APPLICANTS } from "../../utils/routeConstants";
import { mapDictionaryOptions } from "../../utils/functions";
import { dateTimePicker } from "../../reducers/TimeReducer";

export const useApplicantData = (id, reset) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userAgency = useSelector((state) => state.user.userAgency);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const [yearAppliedOptions, setYearAppliedOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const statusOptions = mapDictionaryOptions(dictionary.codePerApplicantStatus);
  const applicantTestResultOptions = mapDictionaryOptions(
    dictionary.codePerApplicantTestResult
  );
  const raceOptions = mapDictionaryOptions(dictionary.codeRace);
  const genderOptions = mapDictionaryOptions(dictionary.codeSex);
  const ethnicityOptions = mapDictionaryOptions(dictionary.codeEthnicity);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);
  const maritalOptions = mapDictionaryOptions(dictionary.codeMaritalStatus);
  const educationOptions = mapDictionaryOptions(dictionary.codePerEducation);
  const stateOptions = mapDictionaryOptions(dictionary.codeStates);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        dispatch(setCircularLoading(true));
        setLoaded(false);
        const services = {
          yearOptions: getService(serviceNames.perApplicantYearOptions),
        };

        const results = await Promise.all([
          services.yearOptions.find({
            query: {
              agencyId: userAgency,
            },
          }),
        ]);
        if (isMounted) {
          if (Array.isArray(results[0])) {
            setYearAppliedOptions(
              results[0].map((value) => ({
                label: value,
                value,
              }))
            );
          }
        }

        if (id) {
          const service = getService(serviceNames.perApplicant);

          const resultData = await service.find({
            query: {
              id: id,
              agencyId: userAgency,
            },
          });
          
          const result = resultData?.data?.[0] || null;

          if (!result) {
            setTimeout(() => {
              dispatch(notify("You don't have access to this applicant.", "warning"));
            }, 1000);
            history.go(-1);
            return;
          }

          if (isMounted && result) {
            reset({
              id: result.id || null,
              suffix: result.suffix || "",
              lastName: result.lastName || "",
              firstName: result.firstName || "",
              middleName: result.middleName || "",
              dob: result?.dob ? dateTimePicker(result.dob) : "",
              ssn: result.ssn || "",
              ethnicity: result.ethnicity || "",
              gender: result.gender || "",
              race: result.race || "",
              maritalStatus: result.maritalStatus || "",
              phone: result.phone || "",
              email: result.email || "",
              workPhoneNum: result.workPhoneNum || "",
              priorLawEnforcement: result.priorLawEnforcement || false,
              prepSchool: result.prepSchool || false,
              address: result.address || "",
              city: result.city || "",
              state: result.state || "",
              zip: result.zip || "",
              comments: result.comments || "",
              militaryRelatedNotes: result.militaryRelatedNotes || "",
              education: result.education || "",
              pat: result.pat || "",
              patDate: result.patDate
                ? dateTimePicker(result.patDate)
                : "",
              patNotes: result.patNotes || null,
              written: result.written || "",
              writtenDate: result.writtenDate
                ? dateTimePicker(result.writtenDate)
                : "",
              writtenNotes: result.writtenNotes || null,
              polygraph: result.polygraph || "",
              polygraphDate: result.polygraphDate
                ? dateTimePicker(result.polygraphDate)
                : "",
              polygraphNotes: result.polygraphNotes || null,
              background: result.background || "",
              backgroundDateSent: result.backgroundDateSent
                ? dateTimePicker(result.backgroundDateSent)
                : "",
              backgroundDateReturned: result.backgroundDateReturned
                ? dateTimePicker(result.backgroundDateReturned)
                : "",
              backgroundNotes: result.backgroundNotes || null,
              backgroundInvestigator: result.backgroundInvestigator || null,
              oralCommittee: result.oralCommittee || "",
              oralCommitteeDate: result.oralCommitteeDate
                ? dateTimePicker(result.oralCommitteeDate)
                : "",
              oralCommitteeNotes: result.oralCommitteeNotes || null,
              oralDirector: result.oralDirector || "",
              oralDirectorDate: result.oralDirectorDate
                ? dateTimePicker(result.oralDirectorDate)
                : "",
              oralDirectorNotes: result.oralDirectorNotes || null,
              eligibility: result.eligibility || false,
              medical: result.medical || "",
              medicalDate: result.medicalDate
                ? dateTimePicker(result.medicalDate)
                : "",
              medicalNotes: result.medicalNotes || "",
              psych: result.psych || "",
              psychDate: result.psychDate
                ? dateTimePicker(result.psychDate)
                : "",
              psychNotes: result.psychNotes || null,
              boxNum: result.boxNum || "",
              yearApplied: result.yearApplied || "",
              status: result.status || "",
              testNotes: result.testNotes || "",
            });
          }
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          dispatch(notify("Failed to fetch applicant", "error"));
          history.push(PATH_APPLICANTS);
        }
      } finally {
        dispatch(setCircularLoading(false));
        setLoaded(true);
      }
    };

    init();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch, reset, userAgency]);
  return {
    genderOptions,
    ethnicityOptions,
    maritalOptions,
    stateOptions,
    statusOptions,
    applicantTestResultOptions,
    educationOptions,
    yearAppliedOptions,
    raceOptions,
    tagTypeOptions,
    suffixOptions,
    loaded,
  };
};
